/**
 * IMPORTANT: This file is only for handling instore module.
 * Other modules are now managed in the merchant-portal project.
 *
 * Authentication is already handled in merchant-portal - the implementation
 * here is legacy only and will be removed in the future.
 * Only URLs will be maintained.
 *
 * Permissions here are only for instore and use legacy permission_scopes.
 * New permissions are in permission_scopes_v2.
 *
 * DO NOT MODIFY localStorage keys - validate if other modules besides instore
 * use them, as these keys are shared across all modules.
 *
 * If you need to modify login auth functionality, please consult with the
 * merchant-services team.
 */
import { useState } from 'react';
import { usePermissionContext } from '../../contexts/Role/PermissionContext';
import { OTP_REQUIRED, loginErrors } from '../constants';
import { useQueryClient } from '@tanstack/react-query';

import Auth from './auth';
import AnalyticsEvent from '../../services/analytics/AnalyticsEvent';
import { useStore } from '../../services/store';
import Requestor from '../fetchHTTP/Requestor';
import { getHostAppUrl } from '../environment';
import { isFeatureEnabled } from '../Feature/feature_flag';

export const useProvideAuth = () => {
  const { merchantTransactionsReport } = useStore();
  const queryClient = useQueryClient();
  const [user, setUser] = useState(() =>
    Auth.userLoggedIn() ? Auth.userData() : null
  );
  const { loadPermissions, clearPermissions, setHotjarIdentified } =
    usePermissionContext();

  const getEmployeeInfo = (data, loginData) => {
    if (data && data.employee && loginData && loginData.email) {
      return {
        ...data.employee,
        email: loginData.email,
      };
    }

    return data.employee;
  };

  const login = async loginData => {
    try {
      const response = await Auth.login(loginData);
      Auth.saveAuthInfo({
        ...response,
        employee: getEmployeeInfo(response, loginData),
      });
      setUser(response);
      loadPermissions();
      merchantTransactionsReport.resetStore();
      AnalyticsEvent().logInComplete();
      return response;
    } catch (error) {
      if (error?.response?.data?.message === OTP_REQUIRED) {
        AnalyticsEvent().errorTrack({
          errorType: OTP_REQUIRED,
          errorDescription: error.response.data,
        });

        return { error: error.response.data };
      }

      AnalyticsEvent().errorTrack({
        errorType: 'login-error',
        errorDescription: error,
      });

      return {
        error:
          loginErrors[error.code] ||
          'Encontramos un error.\n¿Lo puedes volver a intentar?',
        data: error,
      };
    }
  };

  const passwordRecover = recoverData => {
    return Auth.passwordRecover(recoverData)
      .then(response => {
        return response;
      })
      .catch(error => {
        AnalyticsEvent().errorTrack({
          errorType: 'passwordRecover-error',
          errorDescription: error,
        });

        console.log(error);
        return { error: 'Ocurrio un error: intente de nuevo' };
      });
  };

  const logout = async () => {
    setHotjarIdentified(false);
    try {
      await Auth.logout();
      await Requestor().getRequest({
        method: 'POST',
        url: `${getHostAppUrl()}/api/auth/signout`,
        extraOptions: {
          disableBaseUrl: true,
        },
      });
      queryClient.removeQueries();
      clearPermissions();
      setUser(false);
    } catch (error) {
      AnalyticsEvent().errorTrack({
        errorType: 'logout-error',
        errorDescription: error,
      });
    } finally {
      window.location.replace(
        isFeatureEnabled('enabled_new_portal_urls')
          ? `${getHostAppUrl()}/login`
          : '/login'
      );
    }
  };

  return {
    user,
    login,
    logout,
    passwordRecover,
  };
};
